import React from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import {
  LayoutGrid, Users, Package, DollarSign, Coins, AlertOctagon, Settings2,
  CreditCard, PackageSearch, Layers, Building2, FileText, ShieldCheck,
  Undo2, Radio, KeyRound, Menu, X,
} from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/lib/i18n";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminCredits from "@/pages/admin/AdminCredits";
import AdminIncidents from "@/pages/admin/AdminIncidents";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCreem from "@/pages/admin/AdminCreem";
import AdminCreemProducts from "@/pages/admin/AdminCreemProducts";
import AdminResources from "@/pages/admin/AdminResources";
import AdminCompany from "@/pages/admin/AdminCompany";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminConsents from "@/pages/admin/AdminConsents";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminWebhooks from "@/pages/admin/AdminWebhooks";
import AdminCases from "@/pages/admin/AdminCases";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminCustomer from "@/pages/admin/AdminCustomer";
import AdminReconciliation from "@/pages/admin/AdminReconciliation";
import AdminSessions from "@/pages/admin/AdminSessions";
import AdminProductsV2 from "@/pages/admin/AdminProductsV2";
import AdminPlansV2 from "@/pages/admin/AdminPlansV2";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBulkOps from "@/pages/admin/AdminBulkOps";
import AdminSecurityOps from "@/pages/admin/AdminSecurityOps";
import CommandPalette from "@/components/CommandPalette";

function SidebarNav({ groups }) {
  return (
    <nav className="lp-card p-4 h-fit space-y-4" aria-label="Admin sections">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="text-[10px] uppercase tracking-widest text-white/40 px-2 pb-2">{g.label}</div>
          {g.items.map((it) => (
            <NavLink
              key={it.id}
              to={`/admin/${it.to}`}
              end={it.end}
              className={({ isActive }) => `lp-sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`admin-nav-${it.id}`}
            >
              <it.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{it.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

export default function AdminPage() {
  const { t } = useLang();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Close mobile drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Escape closes drawer
  React.useEffect(() => {
    if (!mobileOpen) return;
    const h = (e) => e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [mobileOpen]);

  const groups = [
    { label: t("admin.section.overview"), items: [
      { to: "", icon: LayoutGrid, label: t("admin.overview"), end: true, id: "overview" },
      { to: "analytics", icon: LayoutGrid, label: t("admin.analytics"), id: "analytics" },
    ]},
    { label: t("admin.section.customers"), items: [
      { to: "users", icon: Users, label: t("admin.users"), id: "users" },
      { to: "bulk", icon: Layers, label: t("admin.bulk"), id: "bulk" },
    ]},
    { label: t("admin.section.commerce"), items: [
      { to: "products-v2", icon: Package, label: t("admin.products_v2"), id: "products-v2" },
      { to: "plans-v2", icon: DollarSign, label: t("admin.plans_v2"), id: "plans-v2" },
      { to: "credit-packs", icon: Coins, label: t("admin.credit_packs"), id: "credits" },
      { to: "refunds", icon: Undo2, label: t("admin.refunds"), id: "refunds" },
      { to: "cases", icon: Undo2, label: t("admin.cases"), id: "cases" },
      { to: "resources", icon: PackageSearch, label: t("admin.resources"), id: "resources" },
    ]},
    { label: t("admin.section.operations"), items: [
      { to: "reconciliation", icon: ShieldCheck, label: t("admin.reconciliation"), id: "reconciliation" },
      { to: "webhooks", icon: Radio, label: t("admin.webhooks"), id: "webhooks" },
      { to: "incidents", icon: AlertOctagon, label: t("admin.incidents"), id: "incidents" },
      { to: "creem", icon: CreditCard, label: t("admin.creem"), id: "creem" },
      { to: "creem-products", icon: Layers, label: t("admin.creem_products"), id: "creem-products" },
    ]},
    { label: t("admin.section.security"), items: [
      { to: "sessions", icon: KeyRound, label: t("admin.sessions"), id: "sessions" },
      { to: "security-ops", icon: ShieldCheck, label: t("adminp4.title"), id: "security-ops" },
      { to: "audit", icon: ShieldCheck, label: t("admin.audit"), id: "audit" },
      { to: "consents", icon: ShieldCheck, label: t("admin.consents"), id: "consents" },
    ]},
    { label: t("admin.section.config"), items: [
      { to: "settings", icon: Settings2, label: t("admin.settings"), id: "settings" },
      { to: "company", icon: Building2, label: t("admin.company"), id: "company" },
      { to: "legal", icon: FileText, label: t("admin.legal"), id: "legal" },
    ]},
  ];

  const sidebarNode = <SidebarNav groups={groups} />;

  return (
    <div className="admin">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <CommandPalette />

        {/* Mobile: sticky topbar with menu toggle */}
        <div className="lg:hidden flex items-center gap-2 mb-4">
          <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="admin-nav-open"><Menu className="w-4 h-4" /> Menu</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          <aside className="hidden lg:block lg:sticky lg:top-24 self-start">{sidebarNode}</aside>

          {/* Mobile drawer */}
          {mobileOpen && (
            <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} role="dialog" aria-modal="true">
              <div className="w-72 h-full bg-[#141417] overflow-y-auto p-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end mb-2">
                  <button className="lp-btn-ghost" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X className="w-4 h-4" /></button>
                </div>
                {sidebarNode}
              </div>
            </div>
          )}

          <main className="min-w-0">
            <Routes>
              <Route index element={<AdminOverview />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="products-v2" element={<AdminProductsV2 />} />
              <Route path="plans-v2" element={<AdminPlansV2 />} />
              <Route path="bulk" element={<AdminBulkOps />} />
              <Route path="security-ops" element={<AdminSecurityOps />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="customers/:user_id" element={<AdminCustomer />} />
              <Route path="reconciliation" element={<AdminReconciliation />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="credit-packs" element={<AdminCredits />} />
              <Route path="creem" element={<AdminCreem />} />
              <Route path="creem-products" element={<AdminCreemProducts />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
              <Route path="cases" element={<AdminCases />} />
              <Route path="refunds" element={<AdminRefunds />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="company" element={<AdminCompany />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="consents" element={<AdminConsents />} />
              <Route path="incidents" element={<AdminIncidents />} />
              <Route path="sessions" element={<AdminSessions />} />
              <Route path="settings" element={<AdminSettings />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
